import { useMemo, useState, type ReactNode } from "react";
import { ApiError, errorMessage } from "../../api/client";
import { useCompany, useCompanyHistory, usePatchCompany } from "../../api/hooks";
import type { CompanyDetail, ContactPointOut, FieldDef, QualityMap } from "../../api/types";
import { CHANGE, Chip, Drawer, Empty, FUNCTIONS, Icon, QUALITY, QualityDot, SOURCE, Segmented, Spinner, useToast } from "../../components/ui";
import { dateTime, display, siret } from "../../lib/format";
import { useAuth } from "../../lib/auth";

const KIND: Record<string, string> = { phone: "Tél.", email: "Email", website: "Site", linkedin: "LinkedIn" };
const CORE_ORDER = ["siret", "siren", "name", "address_line4", "postal_code", "city", "department", "naf_code", "naf_label",
  "headcount_band", "trade", "sub_trade", "call_center_names", "campaign_names"];
const READONLY = new Set(["department", "status"]);

function Points({ points }: { points: ContactPointOut[] }) {
  if (!points.length) return <span className="muted">Aucune coordonnée</span>;
  return (
    <div className="cps">
      {points.map((p) => (
        <div key={p.id} className="cp">
          <span className="kind">{p.subtype === "mobile" ? "Mobile" : KIND[p.kind]}</span>
          <QualityDot q={p.quality} reason={p.reason} />
          {p.kind === "email" ? <a href={`mailto:${p.value}`}>{p.value}</a>
            : p.kind === "website" || p.kind === "linkedin" ? <a href={p.value} target="_blank" rel="noopener noreferrer">{p.value}</a>
              : <span className="num">{p.value}</span>}
          {p.quality !== "valid" && <span className="reason">{p.reason ?? QUALITY[p.quality].label}</span>}
        </div>
      ))}
    </div>
  );
}

function QualityCell({ q, k, children }: { q: QualityMap; k: string; children: ReactNode }) {
  const e = q[k];
  return (
    <>
      {e && e.s !== "valid" && <QualityDot q={e.s} reason={e.r} />}
      <span>{children}</span>
      {e && e.s !== "valid" && (e.r || e.raw) && <span className="qnote">{e.r}{e.raw ? ` Valeur reçue : « ${e.raw} »` : ""}</span>}
    </>
  );
}

function Sheet({ c, fields, editing, draft, setDraft }: { c: CompanyDetail; fields: FieldDef[]; editing: boolean; draft: Record<string, string>; setDraft: (d: Record<string, string>) => void }) {
  const byKey = useMemo(() => Object.fromEntries(fields.filter((f) => f.entity === "company").map((f) => [f.key, f])), [fields]);
  const core = CORE_ORDER.filter((k) => byKey[k]);
  const extra = fields.filter((f) => f.entity === "company" && f.storage === "attrs" && f.status !== "archived");
  const unknownAttrs = Object.keys(c.attrs ?? {}).filter((k) => !byKey[k]);
  const input = (k: string, v: unknown) => (
    <input className="input sm" style={{ width: "100%" }} aria-label={byKey[k]?.label ?? k}
      value={draft[k] ?? (Array.isArray(v) ? v.join(", ") : v == null ? "" : String(v))}
      onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} />
  );
  const val = (k: string) => (c as unknown as Record<string, unknown>)[k];
  return (
    <>
      <div className="kv">
        {core.map((k) => (
          <FragmentRow key={k} label={byKey[k].label}>
            {editing && !READONLY.has(k) ? input(k, val(k)) : <QualityCell q={c.quality} k={k}>{k === "siret" ? <span className="mono">{siret(c.siret)}</span> : display(val(k))}</QualityCell>}
          </FragmentRow>
        ))}
      </div>
      <h3>Coordonnées de l'entreprise</h3>
      <Points points={c.contact_points} />
      <div className="row between"><h3>Données complémentaires</h3><span className="muted" style={{ fontSize: 13 }}>ajoutées au fil des imports</span></div>
      {extra.length + unknownAttrs.length === 0 ? <span className="muted">Aucune</span> : (
        <div className="kv">
          {extra.map((f) => (
            <FragmentRow key={f.key} label={<>{f.label}{f.origin === "source" && <Chip tone="blue">import</Chip>}</>}>
              {editing ? input(f.key, c.attrs?.[f.key]) : <QualityCell q={c.quality} k={`attrs.${f.key}`}>{display(c.attrs?.[f.key])}</QualityCell>}
            </FragmentRow>
          ))}
          {unknownAttrs.map((k) => <FragmentRow key={k} label={k}>{display(c.attrs[k])}</FragmentRow>)}
        </div>
      )}
    </>
  );
}

function FragmentRow({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (<><div className="k">{label}</div><div className="v">{children}</div></>);
}

function History({ id, contacts, fields }: { id: number; contacts: CompanyDetail["contacts"]; fields: FieldDef[] }) {
  const h = useCompanyHistory(id, true);
  const label = (entity: string, key: string) => {
    const k = key.replace(/^attrs\./, "");
    return fields.find((f) => f.entity === entity && f.key === k)?.label ?? fields.find((f) => f.key === k)?.label ?? k;
  };
  const names = Object.fromEntries(contacts.map((ct) => [ct.id, [ct.first_name, ct.last_name].filter(Boolean).join(" ")]));
  if (h.isLoading) return <Spinner />;
  if (!h.data?.length) return <Empty title="Aucun historique" />;
  return (
    <div className="timeline">
      {h.data.map((e, i) => {
        const ch = CHANGE[e.change] ?? { label: e.change, tone: "gray" as const };
        return (
          <div key={i} className="tl">
            <time dateTime={e.at}>{dateTime(e.at)}</time>
            <div className="stack" style={{ gap: 4 }}>
              <div className="chg">
                <Chip tone={ch.tone}>{ch.label}</Chip>
                <b>{e.field === "*" ? (e.entity === "contact" ? `Contact ${names[e.entity_id] ?? ""}` : "Fiche entreprise") : label(e.entity, e.field)}</b>
                {e.entity === "contact" && e.field !== "*" && <span className="muted">· {names[e.entity_id] ?? "contact"}</span>}
              </div>
              {e.field !== "*" && (
                <div className="chg">
                  {e.old != null && <span className={e.change === "conflict" ? "" : "old"}>{display(e.old)}</span>}
                  {e.new != null && <><span className="faint">{e.change === "conflict" ? "· valeur reçue non appliquée :" : "→"}</span><b>{display(e.new)}</b></>}
                </div>
              )}
              <span className="muted" style={{ fontSize: 12.5 }}>{SOURCE[e.source] ?? e.source}{e.import_id ? ` · import #${e.import_id}` : ""}{e.mission_id ? ` · mission #${e.mission_id}` : ""}{e.user_id ? ` · utilisateur #${e.user_id}` : ""}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function CompanyDrawer({ id, onClose, fields }: { id: number | null; onClose: () => void; fields: FieldDef[] }) {
  const q = useCompany(id);
  const me = useAuth();
  const [tab, setTab] = useState<"sheet" | "contacts" | "history">("sheet");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [conflict, setConflict] = useState(false);
  const patch = usePatchCompany(id ?? 0);
  const toast = useToast();
  const c = q.data;

  const close = () => {
    setEditing(false);
    setDraft({});
    setConflict(false);
    setTab("sheet");
    onClose();
  };

  async function save() {
    if (!c) return;
    const changes: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(draft)) {
      changes[k] = ["call_center_names", "campaign_names"].includes(k) ? v.split(",").map((x) => x.trim()).filter(Boolean) : v;
    }
    try {
      await patch.mutateAsync({ version: c.version, changes });
      toast("Fiche enregistrée · historique mis à jour");
      setEditing(false);
      setDraft({});
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setConflict(true);
      else toast(errorMessage(e), true);
    }
  }

  const subtitle = c && !c.merged_into_id && (
    <div className="row" style={{ gap: 6 }}>
      <span className="mono muted">{siret(c.siret)}</span>
      {c.department && <Chip tone="gray">dép. {c.department}</Chip>}
      {c.campaign_names?.map((n) => <Chip key={n} tone="purple">{n}</Chip>)}
    </div>
  );
  const actions = c && !c.merged_into_id && me.can("data.write") && tab === "sheet" && (
    editing ? (
      <>
        <button className="btn sm ghost" type="button" onClick={() => { setEditing(false); setDraft({}); }}>Annuler</button>
        <button className="btn sm primary" type="button" onClick={save} disabled={!Object.keys(draft).length || patch.isPending}><Icon name="check" />Enregistrer</button>
      </>
    ) : <button className="btn sm" type="button" onClick={() => setEditing(true)}><Icon name="edit" />Modifier</button>
  );

  return (
    <Drawer open={id != null} onClose={close} title={c?.name ?? (q.isLoading ? "Chargement…" : "Entreprise")} subtitle={subtitle} actions={actions}>
      {q.isLoading && <Spinner />}
      {q.error && <div className="banner bad">{errorMessage(q.error)}</div>}
      {c?.merged_into_id && <div className="banner info">Cette fiche a été fusionnée dans l'entreprise #{c.merged_into_id}. Elle est conservée pour l'historique.</div>}
      {c && !c.merged_into_id && (
        <>
          <Segmented label="Sections" value={tab} onChange={setTab}
            options={[["sheet", "Fiche"], ["contacts", `Contacts (${c.contacts.length})`], ["history", "Historique"]]} />
          {conflict && (
            <div className="banner warn" role="alert">
              <Icon name="alert" />
              <span>Quelqu'un a modifié cette fiche entre-temps. Vos changements ne sont pas perdus : rechargez la fiche pour les appliquer sur la dernière version.
                <button className="btn sm" type="button" style={{ marginLeft: 8 }} onClick={() => { setConflict(false); q.refetch(); }}>Recharger</button></span>
            </div>
          )}
          {tab === "sheet" && <Sheet c={c} fields={fields} editing={editing} draft={draft} setDraft={setDraft} />}
          {tab === "contacts" && (c.contacts.length ? c.contacts.map((ct) => (
            <div key={ct.id} className="contact">
              <div className="row between">
                <div className="stack" style={{ gap: 2 }}>
                  <b style={{ fontSize: 16 }}>{[ct.civility, ct.first_name, ct.last_name].filter(Boolean).join(" ") || "Contact sans nom"}</b>
                  <span className="muted">{ct.job_title ?? "Fonction inconnue"}</span>
                </div>
                <div className="row">
                  {ct.is_primary_executive && <Chip tone="purple" dot>Dirigeant principal</Chip>}
                  {ct.function_code && <Chip tone="blue">{FUNCTIONS[ct.function_code] ?? ct.function_code}</Chip>}
                  {ct.status === "left" && <Chip tone="gray">A quitté l'entreprise</Chip>}
                </div>
              </div>
              <Points points={ct.contact_points} />
              {Object.keys(ct.attrs ?? {}).length > 0 && (
                <div className="kv">{Object.entries(ct.attrs).map(([k, v]) => <FragmentRow key={k} label={fields.find((f) => f.entity === "contact" && f.key === k)?.label ?? k}>{display(v)}</FragmentRow>)}</div>
              )}
            </div>
          )) : <Empty title="Aucun contact" />)}
          {tab === "history" && <History id={c.id} contacts={c.contacts} fields={fields} />}
          <span className="faint" style={{ fontSize: 12.5 }}>Créée le {dateTime(c.created_at)} · mise à jour le {dateTime(c.updated_at)} · version {c.version}</span>
        </>
      )}
    </Drawer>
  );
}
